import { updateField } from 'vuex-map-fields';
import { cloneDeep } from 'lodash';
import { widgetBasicSettings } from './../widget-helper';

export const mutations = {
  updateField,
  /**
   * Mutation to set widget
   */
  setWidget(state, widget) {
    state.widget = widget;
    /**
     * Copy current widget into previous widget to detect changes
     */
    state.previousWidget = cloneDeep(widget);
  },
  /**
   * Mutation to set business rules
   */
  setBusinessRules(state, businessRules) {
    state.businessRules = businessRules;
  },

  /**
   * Mutation to set kpis
   */
  setKpis(state, kpis) {
    state.kpis = kpis;
  },

  /**
   * Mutation to set dataModel
   */
  setDataModel(state, { dataModelName, dataModelUUID }) {
    state.widget.BusinessRuleDataTable = dataModelName;
    state.dataModelUuid = dataModelUUID;
  },
  /**
   * Mutation to reset kpis and businessRuleColumn arrays
   */
  resetBrDependentLists(state) {
    state.kpis = [];
    state.businessRuleColumn = [];
    state.dataModelUuid = null;
    state.widget.BusinessRuleDataTable = null;
  },

  setBusinessRuleColumn(state, columns = []) {
    state.businessRuleColumn = columns;
  },

  setFieldsLoadingStatus(state, loaded) {
    state.fieldsLoaded = loaded;
  },

  setKpisLoadingStatus(state, loaded) {
    state.kpisLoaded = loaded;
  },

  /**Mutation to add series */
  addSeries(state) {
    let seriesObj = { Settings: {} };
    if (['Chart', 'KPI History'].includes(state.widget.type)) {
      if (state.widget.chartType === 'SpiderWeb Chart') {
        seriesObj.Settings.type = 'line';
      }
      if (state.widget.chartType === 'Combo Chart') {
        seriesObj.VisualizationType = 'Line';
      }
      state.widget.Settings.orderLabelColor.push({});
      state.widget.groupBy2 = null;
    }
    state.widget.Series.push(seriesObj);
    state.openSeriesPanel = state.widget.Series.length - 1;
  },
  /**Mutation to remove series */
  removeSeries(state, index) {
    state.seriesRenderKey++;
    state.widget.Series.splice(index, 1);
    if (['Chart', 'KPI History'].includes(state.widget.type)) {
      state.widget.Settings.orderLabelColor.splice(index, 1);
      if (state.widget.Series.length === 1) {
        state.widget.Series[0].SecondaryAxis = false;
      }
    }
  },

  /**
   * Mutation to set openSeriesPanel
   */
  setOpenSeriesPanel(state, value) {
    state.openSeriesPanel = value;
  },
  /**
   * Mutation to set series specific fields
   */
  setSeriesSpecificFields(state, fields) {
    state.seriesSpecificFields = fields;
  },
  /**
   * mutation to add drill-down dimension
   */
  addDrillDownDimension(state) {
    state.widget.groupByDrilldown.push('');
    state.widget.Series = state.widget.Series.map((series) => ({
      ...series,
      ErrorBar: false,
      LowerKpiRef: null,
      UpperKpiRef: null
    }));
  },
  /**
   * Mutation to remove drill-down dimension
   */
  removeDrillDownDimension(state, index) {
    state.widget.groupByDrilldown.splice(index, 1);
  },

  resetFields(state) {
    state.widget.groupBy = null;
    state.widget.groupBy2 = null;
    state.widget.Series = [{ Settings: {} }];
    if (state.widget.type === 'Chart') {
      state.widget.Settings = { orderLabelColor: [{}], showDecimalAxes: true };
      state.widget.groupByDrilldown = [];
      switch (state.widget.chartType) {
        case 'Gantt Chart':
          state.widget.Settings = { ganttChartColorsMap: [] };
          break;
        case 'SpiderWeb Chart':
          state.widget.Series[0].Settings.type = 'line';
          break;
        case 'Combo Chart':
          state.widget.Series[0].VisualizationType = 'Line';
          break;
        case 'Matrix Chart':
          state.widget.Settings = {
            orderLabelColor: [{}],
            matrixWidgetSettings: { filterWidgets: {}, manualColors: [] }
          };
          break;
      }
    } else if (state.widget.type === 'Hierarchical Menu') {
      state.widget.Settings = widgetBasicSettings()[state.widget.type];
    } else if (state.widget.type === 'Financial Table') {
      state.widget.groupByDrilldown = [];
      state.widget.orderBy = null;
      state.widget.orderByType = null;
      state.widget.Settings.splitResultsBy = null;
    }
    state.openSeriesPanel = 0;
  },
  /**
   * Sets percentageType to false when stack-column is unselected.
   */
  setPercentageType(state, stackColumn) {
    if (!stackColumn) {
      state.widget.Series[0].isPercentageType = false;
    }
  },
  /**
   * Resets field which dependable on Dimension2 when unselected.
   */
  resetDimension2Fields(state) {
    delete state.widget.Series[0].StackColumn;
    delete state.widget.Series[0].isPercentageType;
  },

  /**
   * Copy the current widget into previous widget.
   */
  setPreviousWidget(state, widget) {
    state.previousWidget = cloneDeep(widget);
  },

  /**
   * Mutation to set field values for gantt chart color column
   */
  setFieldValues(state, fieldValues) {
    state.fieldValues = fieldValues;
  },

  /**
   * Mutation to add gantt chart color
   */
  addGanttChartColor(state) {
    state.widget.Settings.ganttChartColorsMap.push({});
    state.openColorPanel = state.widget.Settings.ganttChartColorsMap.length - 1;
  },
  /**
   * Mutation to remove gannt chart color configuration
   */
  removeGanttChartColor(state, index) {
    state.widget.Settings.ganttChartColorsMap.splice(index, 1);
  },

  resetDataTableFields(state) {
    if (state.widget.type === 'Data Table') {
      state.widget.Settings = {
        fields: [],
        isCollapsible: false,
        showRowSummary: { columns: [] },
        format: {},
        formatBydate: [],
        attachmentDetails: {},
        attachmentDownloadDetails: {},
        selectedFormat: {},
        unfoldableRowSettings: {}
      };
    }
  },
  /**
   * Mutation to set widgets for dashboard
   */
  setDashboardWidgets(state, widgets) {
    state.dashboardWidgets = widgets.filter((widget) => widget.UUID !== state.widget.UUID);
  },
  /**
   * Mutation to add world-map color rule.
   */
  addWorldMapRule(state) {
    let ruleObj = { condition: null, colorV2: 'default' };
    if (!state.widget.Settings.worldMapSeriesColor.rules) {
      state.widget.Settings.worldMapSeriesColor['rules'] = [ruleObj];
      return;
    }
    state.widget.Settings.worldMapSeriesColor['rules'].push(ruleObj);
  },
  /**
   * Mutation to remove world-map color rule.
   */
  removeWorldMapRule(state, index) {
    state.widget.Settings.worldMapSeriesColor['rules'].splice(index, 1);
  },

  /**
   * Sets Fields Map of businessrule
   */
  setFieldsMap(state, { fields, uuid }) {
    state.businessRuleFieldsMap[uuid] = fields;
  },

  /**
   * swaps the color-order-label object when series is drag-dropped.
   */
  swapColorLabel(state, { moved: { oldIndex, newIndex } }) {
    let oldIndexObj = state.widget.Settings.orderLabelColor[oldIndex];
    state.widget.Settings.orderLabelColor.splice(oldIndex, 1);
    state.widget.Settings.orderLabelColor.splice(newIndex, 0, oldIndexObj);
  },

  /**
   * Mutation to reset unfoldableRowSettings
   */

  resetUnfoldableRowSettings(state, unfoldableRowObj) {
    state.widget.Settings.unfoldableRowSettings = unfoldableRowObj;
  },

  /**
   * Mutation to set widgetWidth
   */
  setWidgetWidth(state, width) {
    state.widgetWidth = width;
  },

  /**
   * Set order labels
   */
  setOrderLabel(state, orderLabel) {
    state.widget.Settings.orderLabel = orderLabel;
  },

  /**
   * Set order label color
   */
  setOrderLabelColor(state, orderLabelColor) {
    state.widget.Settings.orderLabelColor = orderLabelColor;
  },

  /**
   * Remove bulk actions from actions.
   */
  removeBulkActions(state) {
    state.widget.AvailableActions = state.widget.AvailableActions.filter((item) => item.type !== 'bulk-actions');
  },

  setWidgetProperty(state, { prop, value }) {
    state.widget[prop] = value;
  }
};
